import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { divisionHasOwnFormat } from "../lib/divisionFormatSummary";
import TournamentFormatConfigSection from "../components/TournamentFormatConfig";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import type { TeamPlayConfig } from "../lib/api";
import {
  api,
  ApiError,
  type Tournament,
  type TournamentDivision,
  type TournamentFormatConfig,
} from "../lib/api";

export default function AdminTournamentSetupFormatPage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();
  const navigate = useNavigate();
  // true = el submit vino del botón lima «Guardar y continuar»; false = solo guardar.
  const continueAfterSaveRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [formatConfig, setFormatConfig] =
    useState<TournamentFormatConfig | null>(null);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [activeDivisionId, setActiveDivisionId] = useState<number | null>(null);
  const [enrolledByDivision, setEnrolledByDivision] = useState<Map<number, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.tournaments.get(token, tournamentId);
      setTournament(response.data.tournament);
      setFormatConfig(response.data.format_config);
      const divisionsResponse = await api.tournaments.listDivisions(token, tournamentId);
      const nextDivisions = divisionsResponse.data.divisions;
      setDivisions(nextDivisions);
      setActiveDivisionId((current) => (
        nextDivisions.some((division) => division.id === current)
          ? current
          : (nextDivisions[0]?.id ?? null)
      ));
      // Inscritos vigentes por categoría: de ahí salen los defaults de grupos del
      // Formato (antes 4 × 7 fijo). Si falla, el Formato sigue con su default.
      try {
        const participantsResponse = await api.participants.list(token, tournamentId);
        const counts = new Map<number, number>();
        participantsResponse.data.participants.forEach((participant) => {
          if (participant.current_status === "dropped") return;
          const key = participant.division_id ?? 0;
          counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        setEnrolledByDivision(counts);
      } catch {
        setEnrolledByDivision(new Map());
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const requestedDivisionId = Number(searchParams.get("division"));
    if (
      Number.isInteger(requestedDivisionId)
      && divisions.some((division) => division.id === requestedDivisionId)
    ) {
      setActiveDivisionId(requestedDivisionId);
    }
  }, [divisions, searchParams]);

  const selectDivision = useCallback((divisionId: number) => {
    setActiveDivisionId(divisionId);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("division", String(divisionId));
      return next;
    });
  }, [setSearchParams]);
  const activeDivision = useMemo(
    () => divisions.find((division) => division.id === activeDivisionId) ?? null,
    [divisions, activeDivisionId],
  );

  const activeFormatConfig = useMemo<TournamentFormatConfig | null>(() => {
    if (!formatConfig) return null;
    if (!activeDivision) return formatConfig;
    return {
      tournament_id: tournamentId,
      format_type: divisionHasOwnFormat(activeDivision) ? (activeDivision.format_type ?? formatConfig.format_type) : formatConfig.format_type,
      config_json: (divisionHasOwnFormat(activeDivision) ? activeDivision.format_config_json : formatConfig.config_json) as TournamentFormatConfig["config_json"],
      created_at: activeDivision.created_at ?? formatConfig.created_at,
      updated_at: activeDivision.updated_at ?? formatConfig.updated_at,
    };
  }, [activeDivision, formatConfig, tournamentId]);

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  if (loading) {
    return (
      <>
        <TournamentSetupProgress current={6} />
        <section className="card">
          <p className="muted">Cargando…</p>
        </section>
      </>
    );
  }

  if (!tournament || !formatConfig) {
    return (
      <>
        <TournamentSetupProgress current={6} />
        <section className="card">
          <p className="form-error">{error ?? "Torneo no encontrado."}</p>
          <Link className="btn btn--ghost" to="/admin/tournaments">
            Volver
          </Link>
        </section>
      </>
    );
  }

  const activeIndex = activeDivision ? divisions.findIndex((division) => division.id === activeDivision.id) : -1;
  const nextDivision = activeIndex >= 0 && activeIndex < divisions.length - 1 ? divisions[activeIndex + 1] : null;

  return (
    <div className="wiz-step wiz-step--formato">
      <TournamentSetupProgress
        current={6}
        tournamentStatus={tournament.status}
        hero={(
          <section className="wiz-hero wiz-hero--navy setup-format-hero">
            <div>
              <p className="eyebrow">{`Paso 6 de 10 · ${activeDivision?.name ?? tournament.name}`}</p>
              <h1>Formato</h1>
              <p className="wiz-hero__lead">Cada categoría tiene su propio formato. Cambia de categoría arriba.</p>
            </div>
            <div className="wiz-hero__actions">
              <Link className="btn btn--ghost" to={`/admin/tournaments/${tournament.id}/setup/registrations`}>
                ← Volver a inscripciones
              </Link>
            </div>
          </section>
        )}
      />

      {error ? <p className="form-error">{error}</p> : null}

      {token && activeFormatConfig && (
        <>
          <TournamentFormatConfigSection
            key={activeDivision?.id ?? "general"}
            token={token}
            tournamentId={tournament.id}
            divisionId={activeDivision?.id ?? null}
            inheritedTeamPlay={(activeDivision?.format_config_json?.team_play as TeamPlayConfig | null | undefined) ?? null}
            enrolledCount={enrolledByDivision.get(activeDivision?.id ?? 0) ?? null}
            initialConfig={activeFormatConfig}
            onChange={(next) => {
              if (activeDivision) {
                setDivisions((current) => current.map((division) => (
                  division.id === activeDivision.id
                    ? {
                      ...division,
                      format_type: next.format_type,
                      format_config_json: next.config_json as Record<string, unknown>,
                    }
                    : division
                )));
              } else {
                setFormatConfig(next);
              }
            }}
            onSaved={() => {
              // «Guardar formato de X» solo guarda; el botón lima avanza.
              if (!continueAfterSaveRef.current) return;
              if (nextDivision) {
                selectDivision(nextDivision.id);
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
              }
              const pending = divisions.filter(
                (division) =>
                  division.id !== activeDivision?.id && !divisionHasOwnFormat(division),
              );
              if (activeDivision && pending.length > 0) {
                // Walk the admin through every category before moving on so
                // none is left silently inheriting the parent format.
                selectDivision(pending[0].id);
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
              }
              navigate(
                `/admin/tournaments/${tournament.id}/setup/ranking${activeDivision ? `?division=${activeDivision.id}` : ""}`,
              );
            }}
            renderActions={({ saving }) => (
              <div className="wiz-bottombar wiz-bottombar--brand fmt-bottombar">
                <Link className="wiz-bottombar__back" to={`/admin/tournaments/${tournament.id}/setup/registrations`}>
                  ‹ Volver a Inscripciones
                </Link>
                <div className="fmt-bottombar__actions">
                  <button
                    className="btn btn--ghost fmt-bottombar__save"
                    type="submit"
                    form="format-config-form"
                    disabled={saving}
                    title={activeDivision ? `Guardar formato de ${activeDivision.name}` : "Guardar formato"}
                    onClick={() => { continueAfterSaveRef.current = false; }}
                  >
                    <span className="fmt-bottombar__label">
                      {saving ? "Guardando…" : activeDivision ? `Guardar formato de ${activeDivision.name}` : "Guardar formato"}
                    </span>
                  </button>
                  <button
                    className="wiz-bottombar__next fmt-bottombar__next"
                    type="submit"
                    form="format-config-form"
                    disabled={saving}
                    title={nextDivision ? `Guardar y continuar con ${nextDivision.name}` : "Guardar y continuar a Ranking"}
                    onClick={() => { continueAfterSaveRef.current = true; }}
                  >
                    <span className="fmt-bottombar__label">
                      {saving
                        ? "Guardando…"
                        : nextDivision
                          ? `Guardar y continuar con ${nextDivision.name} ›`
                          : "Guardar y continuar a Ranking ›"}
                    </span>
                  </button>
                </div>
              </div>
            )}
          />
        </>
      )}
    </div>
  );
}
